import { JobStatus, Report } from '@/api/generatedApi';
import { useGetReport } from '@/hooks/api/reports/useGetReport';
import { LIST_REPORTS_QUERY_KEY } from '@/hooks/api/reports/useFetchListReports';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

const REPORT_POLL_INTERVAL_MS = 3000;

const isInProgress = (status: JobStatus | undefined): boolean =>
  status === JobStatus.PENDING || status === JobStatus.RUNNING;

export const useReportPolling = (parameters: { row: Report }) => {
  const queryClient = useQueryClient();
  const wasInProgressReference = useRef(isInProgress(parameters.row.status));

  const { report: fetchedReport } = useGetReport({
    id: wasInProgressReference.current ? parameters.row.id : undefined,
    refetchInterval: query => {
      const latest = query.state.data?.data.report;
      return isInProgress(latest?.status ?? parameters.row.status) ? REPORT_POLL_INTERVAL_MS : false;
    },
  });

  useEffect(() => {
    if (fetchedReport && wasInProgressReference.current && !isInProgress(fetchedReport.status)) {
      wasInProgressReference.current = false;
      queryClient.resetQueries({ queryKey: [LIST_REPORTS_QUERY_KEY], exact: false });
    }
  }, [fetchedReport, queryClient]);

  return { data: fetchedReport ?? parameters.row, fetchedData: fetchedReport };
};
